import { MOBILE_SCORE_ROLLUP_VALUE } from 'src/apps/dashboard/constants/MobileDashboard.constants';
import { buildDateVariables } from 'src/apps/dashboard/services/queries/dashboard/helpers/dateVariableBuilder';
import {
	DailyUniqueIssuesData,
	DashboardDataResult,
	UniqueIssuesData
} from 'src/apps/dashboard/services/queries/dashboard/helpers/transformResults';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { LabelsExcludedFromDashboard } from 'src/common/types/ConfigurationTypes';
import { ConditionString, ConditionStringArray } from 'src/common/types/graphql.generated';

import {
	MobileDashboardDataQuery,
	MobileDashboardDataQueryVariables
} from '../MobileDashboardData.generated';

import {
	buildMobileBaseVariables,
	buildMobileBuCustomFieldsFilter,
	buildMobilePlatformConditionFilter,
	getMobileAppIdsFromFilters,
	getPlatformFromFilters
} from './mobileFilterVariableBuilder';

const buildUnitIdScoreFilter = (hasFilter: boolean, mobileBuIds?: string[]): ConditionString => {
	if (hasFilter && mobileBuIds?.length) {
		return { value: mobileBuIds };
	}
	return { value: [MOBILE_SCORE_ROLLUP_VALUE] };
};

export const buildMobileDashboardDataVariables = (
	days: number,
	filters?: IFilterOptions,
	labelsExcludedFromDashboard?: LabelsExcludedFromDashboard,
	mobileBuIds?: string[]
): MobileDashboardDataQueryVariables => {
	const dateVariables = buildDateVariables(days);
	const mobileFilters = buildMobileBaseVariables(filters, labelsExcludedFromDashboard);

	const platform = getPlatformFromFilters(filters);
	const appIds = getMobileAppIdsFromFilters(filters);
	const hasFilter = platform !== 'all' || appIds.length > 0;

	const platformMuiFilter = buildMobilePlatformConditionFilter(filters);
	const appIdMuiFilter: ConditionString | undefined = appIds.length ? { value: appIds } : undefined;
	const businessUnitMobileFilter: ConditionString | undefined = mobileBuIds?.length
		? { value: mobileBuIds }
		: undefined;

	const {
		mfaOsFilter: mfaOsCs,
		customFieldsFilter: baseCustomFieldsFilter,
		...restMobileFilters
	} = mobileFilters;

	// MobileDashboardData uses os_eq_any_item_for_any_value which requires ConditionStringArray
	const mfaOsFilter: ConditionStringArray | null = mfaOsCs ? { value: [mfaOsCs.value] } : null;

	// When no user BU selection, restrict to mobile-configured BUs via customFieldsFilter fallback
	const customFieldsFilter: ConditionStringArray | null =
		baseCustomFieldsFilter ?? buildMobileBuCustomFieldsFilter(mobileBuIds) ?? null;

	return {
		days: dateVariables.days,
		days2n: dateVariables.days2n,
		...restMobileFilters,
		mfaOsFilter,
		customFieldsFilter,
		platformScoreFilter:
			platform === 'all' && appIds.length
				? undefined
				: { value: [platform === 'all' ? MOBILE_SCORE_ROLLUP_VALUE : platform] },
		appIdScoreFilter: { value: appIds.length ? appIds : [MOBILE_SCORE_ROLLUP_VALUE] },
		unitIdScoreFilter: buildUnitIdScoreFilter(hasFilter, mobileBuIds),
		platformMuiFilter,
		appIdMuiFilter,
		businessUnitMobileFilter
	};
};

type MfaIssueRow = NonNullable<NonNullable<MobileDashboardDataQuery['mfaIssuesCount']>[number]>;
type MsdkIssueRow = NonNullable<NonNullable<MobileDashboardDataQuery['msdkIssuesCount']>[number]>;
type ScoreRow = NonNullable<
	NonNullable<MobileDashboardDataQuery['scoreMobileProductsDailyTotal']>[number]
>;

const hasValidMfaDate = (
	issue: MfaIssueRow
): issue is MfaIssueRow & { run_updated_at_truncDay: string } =>
	typeof issue.run_updated_at_truncDay === 'string';

const hasValidMsdkDate = (
	issue: MsdkIssueRow
): issue is MsdkIssueRow & { run_time_truncDay: string } =>
	typeof issue.run_time_truncDay === 'string';

const processMfaIssues = (
	issues: MobileDashboardDataQuery['mfaIssuesCount'],
	dateMap: Map<string, { critical: number; serious: number; total: number; allIssues: number }>
): void => {
	(issues ?? []).filter(hasValidMfaDate).forEach((issue) => {
		const date = issue.run_updated_at_truncDay;
		const existing = dateMap.get(date) ?? { critical: 0, serious: 0, total: 0, allIssues: 0 };
		const critical = existing.critical + (issue.critical_issues_sum ?? 0);
		const serious = existing.serious + (issue.serious_issues_sum ?? 0);
		dateMap.set(date, {
			critical,
			serious,
			total: critical + serious,
			allIssues: existing.allIssues + (issue.total_issues_sum ?? 0)
		});
	});
};

const processMsdkIssues = (
	issues: MobileDashboardDataQuery['msdkIssuesCount'],
	dateMap: Map<string, { critical: number; serious: number; total: number; allIssues: number }>
): void => {
	(issues ?? []).filter(hasValidMsdkDate).forEach((issue) => {
		const date = issue.run_time_truncDay;
		const existing = dateMap.get(date) ?? { critical: 0, serious: 0, total: 0, allIssues: 0 };
		const critical = existing.critical + (issue.critical_issues_sum ?? 0);
		const serious = existing.serious + (issue.serious_issues_sum ?? 0);
		dateMap.set(date, {
			critical,
			serious,
			total: critical + serious,
			allIssues: existing.allIssues + (issue.total_issues_sum ?? 0)
		});
	});
};

const combineMobileProductData = (
	mfaData: MobileDashboardDataQuery['mfaIssuesCount'],
	msdkData: MobileDashboardDataQuery['msdkIssuesCount']
): DashboardDataResult['regularPeriod'] => {
	const dateMap = new Map<
		string,
		{ critical: number; serious: number; total: number; allIssues: number }
	>();

	processMfaIssues(mfaData, dateMap);
	processMsdkIssues(msdkData, dateMap);

	return Array.from(dateMap.entries())
		.map(([dateStr, counts]) => ({
			critical: counts.critical,
			serious: counts.serious,
			total: counts.critical + counts.serious,
			allIssues: counts.allIssues,
			day: new Date(dateStr)
		}))
		.sort((a, b) => a.day.getTime() - b.day.getTime());
};

const sumSummaryValues = (
	mfaSummary: MobileDashboardDataQuery['mfaSummary'],
	msdkSummary: MobileDashboardDataQuery['msdkSummary']
): { critical: number; total: number } => {
	const mfaCritical = mfaSummary?.[0]?.critical_issues_sum ?? 0;
	const mfaTotal = mfaSummary?.[0]?.total_issues_sum ?? 0;
	const msdkCritical = msdkSummary?.[0]?.critical_issues_sum ?? 0;
	const msdkTotal = msdkSummary?.[0]?.total_issues_sum ?? 0;

	return {
		critical: mfaCritical + msdkCritical,
		total: mfaTotal + msdkTotal
	};
};

const extractDailyScoreData = (
	scoreRows: MobileDashboardDataQuery['scoreMobileProductsDailyTotal']
): DailyUniqueIssuesData[] => {
	const dateMap = new Map<string, { total: number; critical: number; serious: number }>();

	(scoreRows ?? [])
		.filter((row): row is ScoreRow => row !== null)
		.forEach((row) => {
			const date = row.calculation_date;
			const existing = dateMap.get(date) ?? { total: 0, critical: 0, serious: 0 };
			dateMap.set(date, {
				total: existing.total + (row.issues ?? 0),
				critical: existing.critical + (row.issues_critical ?? 0),
				serious: existing.serious + (row.issues_serious ?? 0)
			});
		});

	return Array.from(dateMap.entries())
		.map(([date, counts]) => ({
			date,
			total: counts.total,
			critical: counts.critical,
			serious: counts.serious
		}))
		.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

const buildUniqueIssuesFromCounts = (rawResults: MobileDashboardDataQuery): UniqueIssuesData => ({
	current: rawResults.mobileUniqueIssuesCount?.[0]?.signature_count ?? 0,
	previous: rawResults.mobileUniqueIssuesPrevCount?.[0]?.signature_count ?? 0,
	critical: {
		current: rawResults.mobileUniqueIssuesCriticalCount?.[0]?.signature_count ?? 0,
		previous: rawResults.mobileUniqueIssuesCriticalPrevCount?.[0]?.signature_count ?? 0
	}
});

const getLastUpdatedAt = (rawResults: MobileDashboardDataQuery): number => {
	const mfaTimestamp = rawResults.mfaSummary?.[0]?.run_updated_at_max;
	const msdkTimestamp = rawResults.msdkSummary?.[0]?.run_time_max;
	const timestamps = [mfaTimestamp, msdkTimestamp]
		.filter(Boolean)
		.map((value) => new Date(value as string).getTime());

	return timestamps.length > 0 ? Math.max(...timestamps) : 0;
};

export const transformMobileDashboardDataResults = (
	rawResults: MobileDashboardDataQuery
): DashboardDataResult => {
	const regularPeriod = combineMobileProductData(
		rawResults.mfaIssuesCount,
		rawResults.msdkIssuesCount
	);
	const doublePeriod = combineMobileProductData(
		rawResults.mfaIssuesCount2n,
		rawResults.msdkIssuesCount2n
	);

	const summary = sumSummaryValues(rawResults.mfaSummary, rawResults.msdkSummary);

	const doubleTotalAllIssues = doublePeriod.reduce((acc, entry) => acc + entry.allIssues, 0);
	const doubleTotalCritical = doublePeriod.reduce((acc, entry) => acc + entry.critical, 0);
	const previousTotal = Math.max(0, doubleTotalAllIssues - summary.total);
	const previousCritical = Math.max(0, doubleTotalCritical - summary.critical);

	const dailyUniqueIssues = extractDailyScoreData(rawResults.scoreMobileProductsDaily);
	const dailyAllIssues = extractDailyScoreData(rawResults.scoreMobileProductsDailyTotal);

	const uniqueIssues = buildUniqueIssuesFromCounts(rawResults);
	const totalIssues = {
		current: summary.total,
		previous: previousTotal,
		critical: {
			current: summary.critical,
			previous: previousCritical
		}
	};

	const dailyRunsIssues = regularPeriod.map((entry) => ({
		date: entry.day.toISOString(),
		total: entry.total,
		critical: entry.critical,
		serious: entry.serious
	}));

	return {
		regularPeriod,
		doublePeriod,
		uniqueIssues,
		totalIssues,
		dailyUniqueIssues: dailyUniqueIssues.length > 0 ? dailyUniqueIssues : dailyRunsIssues,
		dailyAllIssues: dailyAllIssues.length > 0 ? dailyAllIssues : dailyRunsIssues,
		lastUpdatedAt: getLastUpdatedAt(rawResults)
	};
};
